import React, { useState, useEffect } from 'react';
import { cn } from '../lib/utils';
import { SectionHeader } from '../components/ui/SectionHeader';
import { EmptyState } from '../components/ui/EmptyState';
import {
  DollarSign,
  FileText,
  Sparkles,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ArrowRight,
} from 'lucide-react';

// ---- Types ----

type LoanType = 'auto' | 'mortgage' | 'personal' | 'heloc' | 'business' | 'credit_card' | 'student';
type LoanStage = 'application' | 'underwriting' | 'ai_review' | 'approved' | 'denied' | 'funded';

interface LoanRecord {
  id: string;
  memberName: string;
  loanType: LoanType;
  amount: number;
  stage: LoanStage;
  aiSummary: boolean;
  submittedAt: string;
  riskScore: number;
}

// ---- Mock data ----

const MOCK_LOANS: LoanRecord[] = [
  {
    id: 'ln-001',
    memberName: 'Sarah Chen',
    loanType: 'mortgage',
    amount: 285000,
    stage: 'underwriting',
    aiSummary: true,
    submittedAt: new Date(Date.now() - 2 * 3600000).toISOString(),
    riskScore: 24,
  },
  {
    id: 'ln-002',
    memberName: 'Marcus Johnson',
    loanType: 'auto',
    amount: 32500,
    stage: 'ai_review',
    aiSummary: true,
    submittedAt: new Date(Date.now() - 4 * 3600000).toISOString(),
    riskScore: 41,
  },
  {
    id: 'ln-003',
    memberName: 'Priya Nair',
    loanType: 'heloc',
    amount: 75000,
    stage: 'application',
    aiSummary: false,
    submittedAt: new Date(Date.now() - 30 * 60000).toISOString(),
    riskScore: 18,
  },
  {
    id: 'ln-004',
    memberName: 'Derek Fontaine',
    loanType: 'personal',
    amount: 15000,
    stage: 'approved',
    aiSummary: true,
    submittedAt: new Date(Date.now() - 24 * 3600000).toISOString(),
    riskScore: 67,
  },
  {
    id: 'ln-005',
    memberName: 'Linda Okafor',
    loanType: 'mortgage',
    amount: 410000,
    stage: 'application',
    aiSummary: false,
    submittedAt: new Date(Date.now() - 90 * 60000).toISOString(),
    riskScore: 29,
  },
  {
    id: 'ln-006',
    memberName: 'James Rivera',
    loanType: 'auto',
    amount: 28900,
    stage: 'denied',
    aiSummary: true,
    submittedAt: new Date(Date.now() - 48 * 3600000).toISOString(),
    riskScore: 82,
  },
  {
    id: 'ln-007',
    memberName: 'Angela Park',
    loanType: 'heloc',
    amount: 120000,
    stage: 'funded',
    aiSummary: false,
    submittedAt: new Date(Date.now() - 72 * 3600000).toISOString(),
    riskScore: 15,
  },
  {
    id: 'ln-008',
    memberName: 'Robert Kim',
    loanType: 'personal',
    amount: 8500,
    stage: 'underwriting',
    aiSummary: true,
    submittedAt: new Date(Date.now() - 6 * 3600000).toISOString(),
    riskScore: 55,
  },
];

// ---- API types (from crm-api LoanApplication) ----

interface ApiLoanApplication {
  id: string;
  tenantId: string;
  memberId: string;
  memberDisplayName: string;
  loanType: string;
  requestedAmount: number;
  status: string;
  assignedLoanOfficerId: string;
  dqDays: number;
  daysInProcess: number;
  applicationDate: string;
  underwritingSummaryApproved: boolean;
  createdAt: string;
  updatedAt: string;
}

const STATUS_TO_STAGE: Record<string, LoanStage> = {
  pending: 'application',
  in_review: 'underwriting',
  approved: 'approved',
  denied: 'denied',
  withdrawn: 'denied',
  cancelled: 'denied',
};

function mapApiToLoanRecord(api: ApiLoanApplication): LoanRecord {
  return {
    id: api.id,
    memberName: api.memberDisplayName,
    loanType: (api.loanType as LoanType) ?? 'personal',
    amount: api.requestedAmount,
    stage: STATUS_TO_STAGE[api.status] ?? 'application',
    aiSummary: api.underwritingSummaryApproved,
    submittedAt: api.applicationDate,
    riskScore: Math.min(api.dqDays * 10, 100),
  };
}

// ---- Helpers ----

function fmtUSD(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount);
}

function fmtRelative(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ---- Stage config ----

interface StageMeta {
  label: string;
  cardCls: string;
  iconBg: string;
  badgeCls: string;
  Icon: typeof Clock;
}

const STAGE_MAP: Record<LoanStage, StageMeta> = {
  application: {
    label: 'Application',
    cardCls: 'border-t-[3px] border-t-brand',
    iconBg: 'bg-info-soft',
    badgeCls: 'bg-brand-tint text-brand-dark',
    Icon: FileText,
  },
  underwriting: {
    label: 'Underwriting',
    cardCls: 'border-t-[3px] border-t-warn-fill',
    iconBg: 'bg-warn-soft',
    badgeCls: 'bg-warn-soft text-warn',
    Icon: Clock,
  },
  ai_review: {
    label: 'AI Review',
    cardCls: 'border-t-[3px] border-t-running',
    iconBg: 'bg-running-soft',
    badgeCls: 'bg-running-soft text-running-ink',
    Icon: Sparkles,
  },
  approved: {
    label: 'Approved',
    cardCls: 'border-t-[3px] border-t-pass',
    iconBg: 'bg-pass-soft',
    badgeCls: 'bg-pass-soft text-pass',
    Icon: CheckCircle2,
  },
  denied: {
    label: 'Denied',
    cardCls: 'border-t-[3px] border-t-fail',
    iconBg: 'bg-fail-soft',
    badgeCls: 'bg-fail-soft text-fail',
    Icon: XCircle,
  },
  funded: {
    label: 'Funded',
    cardCls: 'border-t-[3px] border-t-pass',
    iconBg: 'bg-pass-soft',
    badgeCls: 'bg-pass-soft text-pass',
    Icon: ArrowRight,
  },
};

// ---- Loan type badge config ----

const LOAN_TYPE_CLS: Record<LoanType, string> = {
  auto: 'bg-brand-tint text-brand-dark',
  mortgage: 'bg-pass-soft text-pass',
  personal: 'bg-warn-soft text-warn',
  heloc: 'bg-running-soft text-running-ink',
  business: 'bg-pass-soft text-pass',
  credit_card: 'bg-brand-tint text-brand-dark',
  student: 'bg-fail-soft text-fail',
};

const LOAN_TYPE_LABEL: Record<LoanType, string> = {
  auto: 'Auto',
  mortgage: 'Mortgage',
  personal: 'Personal',
  heloc: 'HELOC',
  business: 'Business',
  credit_card: 'Credit Card',
  student: 'Student',
};

// ---- Stage indicator card ----

const INDICATOR_STAGES: LoanStage[] = ['application', 'underwriting', 'ai_review', 'approved', 'denied'];

interface StageCardProps {
  stage: LoanStage;
  count: number;
}

function StageIndicatorCard({ stage, count }: StageCardProps): React.ReactElement {
  const meta = STAGE_MAP[stage];
  const { Icon } = meta;
  return (
    <div className={cn('bg-white rounded-xl border border-sunken shadow-sm p-4', meta.cardCls)}>
      <div className="flex items-center gap-3">
        <div className={cn('w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0', meta.iconBg)}>
          <Icon className="h-4 w-4" aria-hidden />
        </div>
        <div>
          <p className="text-xs text-ink-muted font-medium">{meta.label}</p>
          <p className="text-xl font-semibold text-ink">{count}</p>
        </div>
      </div>
    </div>
  );
}

// ---- Loan card ----

interface LoanCardProps {
  loan: LoanRecord;
}

function LoanCard({ loan }: LoanCardProps): React.ReactElement {
  const stageMeta = STAGE_MAP[loan.stage];
  const riskCls = loan.riskScore <= 30
    ? 'text-pass'
    : loan.riskScore <= 60
      ? 'text-warn'
      : 'text-fail';

  return (
    <div className="bg-white rounded-xl border border-sunken shadow-sm p-4">
      <div className="flex flex-wrap items-center gap-2">
        {/* Member name */}
        <span className="font-medium text-ink mr-1">{loan.memberName}</span>

        {/* Loan type badge */}
        <span
          className={cn(
            'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
            LOAN_TYPE_CLS[loan.loanType],
          )}
        >
          {LOAN_TYPE_LABEL[loan.loanType]}
        </span>

        {/* Stage badge */}
        <span
          className={cn(
            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
            stageMeta.badgeCls,
          )}
        >
          <stageMeta.Icon className="h-3 w-3" aria-hidden />
          {stageMeta.label}
        </span>

        {/* AI underwriting badge */}
        {loan.aiSummary && (
          <span
            className={cn(
              'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
              'bg-running-soft text-running-ink',
            )}
          >
            <Sparkles className="h-3 w-3" aria-hidden />
            AI Summary
          </span>
        )}

        {/* Amount + risk score — pushed right */}
        <span className="ml-auto flex items-center gap-3">
          <span className="flex items-center gap-1 text-sm font-semibold text-ink">
            <DollarSign className="h-4 w-4 text-ink-muted" aria-hidden />
            {fmtUSD(loan.amount)}
          </span>
          <span className="flex items-center gap-1 text-xs">
            <AlertTriangle className="h-3 w-3 text-ink-muted" aria-hidden />
            <span className={cn('font-semibold', riskCls)}>{loan.riskScore}</span>
          </span>
          <span className="text-xs text-ink-muted">{fmtRelative(loan.submittedAt)}</span>
        </span>
      </div>
    </div>
  );
}

// ---- LoansPage ----

export function LoansPage(): React.ReactElement {
  const [_filter, setFilter] = useState<LoanStage | 'all'>('all');
  const [showEmpty, setShowEmpty] = useState(false);
  const [loans, setLoans] = useState<LoanRecord[]>(MOCK_LOANS);

  // Fetch from API on mount; fall back to MOCK_LOANS when API unavailable (dev mode)
  useEffect(() => {
    void (async () => {
      try {
        const res = await fetch('/api/loans', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('crm_dev_token') ?? ''}`,
            'x-tenant-id': 'tenant-cascade',
            'x-staff-id': 'staff-001',
            'x-staff-role': 'loan_officer',
          },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as ApiLoanApplication[];
        setLoans(data.map(mapApiToLoanRecord));
      } catch {
        // API not running in dev — keep MOCK_LOANS
        setLoans(MOCK_LOANS);
      }
    })();
  }, []);

  const stageCounts = INDICATOR_STAGES.reduce<Record<string, number>>((acc, stage) => {
    acc[stage] = loans.filter((l) => l.stage === stage).length;
    return acc;
  }, {});

  const displayedLoans = loans;
  const isEmpty = showEmpty || loans.length === 0;

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2">
          <SectionHeader title="Loan Pipeline" />
          <button
            type="button"
            onClick={() => setShowEmpty(!showEmpty)}
            className="text-xs text-ink-muted hover:text-ink-soft ml-2"
          >
            [Toggle empty]
          </button>
        </div>
        <p className="text-sm text-ink-muted mt-1">
          Active applications, underwriting AI summaries, adverse action management
        </p>
      </div>

      {isEmpty ? (
        <EmptyState
          icon={DollarSign}
          headline="No loans in pipeline"
          body="Loan applications from Symitar sync will appear here with AI underwriting summaries."
          ctaLabel="View Members"
          onCta={() => window.location.assign('/members')}
        />
      ) : (
        <>
          {/* Stage indicator cards */}
          <div className="grid grid-cols-5 gap-4">
            {INDICATOR_STAGES.map((stage) => (
              <StageIndicatorCard
                key={stage}
                stage={stage}
                count={stageCounts[stage] ?? 0}
              />
            ))}
          </div>

          {/* Loan cards list */}
          <div className="flex flex-col gap-3">
            {displayedLoans.map((loan) => (
              <LoanCard key={loan.id} loan={loan} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
